import { Box } from '@mui/material'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthContext } from '@/features/auth/context'
import CopilotFab from '@/features/copilot/CopilotFab'
import CopilotPanel from '@/features/copilot/CopilotPanel'
import CopilotSettings from '@/features/copilot/CopilotSettings'
import {
  COPILOT_EXPANDED_MARGIN,
  COPILOT_PANEL_GAP,
  CopilotCorner,
  DEFAULT_COPILOT_SETTINGS,
} from '@/features/copilot/constants'
import useAnyOverlayOpen from '@/features/copilot/useAnyOverlayOpen'
import { useCopilotSettings } from '@/features/copilot/useCopilotSettings'

const Z_INDEX_FAB = 1400
const Z_INDEX_PANEL = 1399
const DRAG_THRESHOLD_PX = 5
const FAB_EDGE_MARGIN_PX = 8

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

interface AnchorStyle {
  position: 'fixed'
  zIndex: number
  top?: number
  bottom?: number
  left?: number
  right?: number
}

const buildFabAnchor = (
  corner: CopilotCorner,
  offsetX: number,
  offsetY: number,
): AnchorStyle => {
  const base: AnchorStyle = { position: 'fixed', zIndex: Z_INDEX_FAB }
  const isTop = corner.startsWith('top-')
  const isLeft = corner.endsWith('-left')
  if (isTop) base.top = offsetY
  else base.bottom = offsetY
  if (isLeft) base.left = offsetX
  else base.right = offsetX
  return base
}

const buildPanelAnchor = (
  corner: CopilotCorner,
  offsetX: number,
  offsetY: number,
): AnchorStyle => {
  const base: AnchorStyle = { position: 'fixed', zIndex: Z_INDEX_PANEL }
  const isTop = corner.startsWith('top-')
  const isLeft = corner.endsWith('-left')
  if (isTop) base.top = offsetY + COPILOT_PANEL_GAP
  else base.bottom = offsetY + COPILOT_PANEL_GAP
  if (isLeft) base.left = offsetX
  else base.right = offsetX
  return base
}

const HIDDEN_ROUTE_PREFIXES = ['/config-setup']

const CopilotWidget = () => {
  const { pathname } = useLocation()
  const { isLogin } = useAuthContext()
  const { settings, updateSettings, resetSettings } = useCopilotSettings()
  const [isOpen, setIsOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
    null,
  )
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const movedRef = useRef(false)
  const isOverlayOpen = useAnyOverlayOpen()
  const isExpanded = settings.expanded === true
  const isDragging = dragStart !== null
  const isRouteHidden = HIDDEN_ROUTE_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  )
  const hideFab = isOverlayOpen || (isOpen && isExpanded)

  const fabAnchor = useMemo(
    () => buildFabAnchor(settings.corner, settings.offsetX, settings.offsetY),
    [settings],
  )

  const panelAnchor = useMemo(
    () => buildPanelAnchor(settings.corner, settings.offsetX, settings.offsetY),
    [settings],
  )

  // When expanded, the panel fills the viewport minus a small margin on each
  // side so that the FAB (top-most z-index) still peeks out and the existing
  // app chrome remains visible at the edges.
  const expandedAnchor = useMemo(
    () => ({
      position: 'fixed' as const,
      zIndex: Z_INDEX_PANEL,
      top: COPILOT_EXPANDED_MARGIN,
      bottom: COPILOT_EXPANDED_MARGIN,
      left: COPILOT_EXPANDED_MARGIN,
      right: COPILOT_EXPANDED_MARGIN,
    }),
    [],
  )

  const handleToggleExpand = useCallback(() => {
    updateSettings({ expanded: !isExpanded })
  }, [isExpanded, updateSettings])

  const handleCollapse = useCallback(() => {
    if (isExpanded) updateSettings({ expanded: false })
  }, [isExpanded, updateSettings])

  const handleResetSize = useCallback(() => {
    updateSettings({
      width: DEFAULT_COPILOT_SETTINGS.width,
      height: DEFAULT_COPILOT_SETTINGS.height,
      expanded: false,
    })
  }, [updateSettings])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      e.currentTarget.setPointerCapture(e.pointerId)
      setDragStart({ x: e.clientX, y: e.clientY })
      setDragOffset({ x: 0, y: 0 })
      movedRef.current = false
    },
    [],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragStart) return
      const dx = e.clientX - dragStart.x
      const dy = e.clientY - dragStart.y
      if (
        !movedRef.current &&
        (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)
      ) {
        movedRef.current = true
      }
      setDragOffset({ x: dx, y: dy })
    },
    [dragStart],
  )

  const handlePointerEnd = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragStart) return
      try {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId)
        }
      } catch (error) {
        console.error('Failed to release pointer capture:', error)
      }

      if (movedRef.current) {
        const rect = e.currentTarget.getBoundingClientRect()
        const vw = window.innerWidth
        const vh = window.innerHeight
        const isLeft = rect.left + rect.width / 2 < vw / 2
        const isTop = rect.top + rect.height / 2 < vh / 2
        const corner: CopilotCorner = `${isTop ? 'top' : 'bottom'}-${
          isLeft ? 'left' : 'right'
        }`
        const maxX = Math.max(0, vw - rect.width - FAB_EDGE_MARGIN_PX)
        const maxY = Math.max(0, vh - rect.height - FAB_EDGE_MARGIN_PX)
        const offsetX = isLeft
          ? clamp(rect.left, FAB_EDGE_MARGIN_PX, maxX)
          : clamp(vw - rect.right, FAB_EDGE_MARGIN_PX, maxX)
        const offsetY = isTop
          ? clamp(rect.top, FAB_EDGE_MARGIN_PX, maxY)
          : clamp(vh - rect.bottom, FAB_EDGE_MARGIN_PX, maxY)
        updateSettings({
          corner,
          offsetX: Math.round(offsetX),
          offsetY: Math.round(offsetY),
        })
      } else {
        setIsOpen((v) => !v)
      }

      setDragStart(null)
      setDragOffset({ x: 0, y: 0 })
    },
    [dragStart, updateSettings],
  )

  const handleToggle = useCallback(() => {
    if (movedRef.current) {
      movedRef.current = false
      return
    }
    setIsOpen((v) => !v)
  }, [])

  if (!isLogin || isRouteHidden) return null

  return (
    <>
      <Box
        sx={{
          ...fabAnchor,
          display: hideFab ? 'none' : 'block',
          cursor: isDragging ? 'grabbing' : 'grab',
          transform: isDragging
            ? `translate(${dragOffset.x}px, ${dragOffset.y}px)`
            : undefined,
          touchAction: 'none',
          userSelect: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
      >
        <CopilotFab open={isOpen} onToggle={handleToggle} />
      </Box>

      <Box
        sx={{
          ...(isExpanded ? expandedAnchor : panelAnchor),
          display: isOpen ? 'block' : 'none',
        }}
      >
        <CopilotPanel
          onClose={() => setIsOpen(false)}
          onOpenSettings={() => setIsSettingsOpen(true)}
          corner={settings.corner}
          width={settings.width}
          height={settings.height}
          offsetX={settings.offsetX}
          offsetY={settings.offsetY}
          expanded={isExpanded}
          onToggleExpand={handleToggleExpand}
          onCollapse={handleCollapse}
          onResize={({ width, height }) => updateSettings({ width, height })}
          onResetSize={handleResetSize}
        />
      </Box>

      <CopilotSettings
        open={isSettingsOpen}
        settings={settings}
        onChange={updateSettings}
        onReset={resetSettings}
        onClose={() => setIsSettingsOpen(false)}
      />
    </>
  )
}

export default CopilotWidget
